/* ===========================================================
   Auth pages — Login, Signup, Forgot password
   =========================================================== */
import React from "react";
import { Logo, toast } from "@/components/shell";
import { Icons } from "@/lib/icons";
import { IMG } from "@/lib/images";
import { useLogin, useSignup } from "@/api/hooks";

const { useState: aUS, useEffect: aUE, useRef: aUR, useMemo: aUM } = React;

/* ----- Role definitions ----- */
const ROLES = [
  {
    k: "customer",
    label: "Customer",
    desc: "Browse OTC, wellness and prescribed products for yourself or your family.",
    icon: "User",
    image: "pharmacyCounter",
    eyebrow: "FOR INDIVIDUALS",
    title: "Verified medicine, delivered to your door.",
    sub: "Sign up in under two minutes. No business credentials needed for OTC and wellness lines — only for scheduled drugs.",
    perks: [
      "Authentic, batch-traceable inventory",
      "Standard 7-day return on most lines",
      "Same-day dispatch on orders before 4 PM IST",
    ],
    fields: ["full_name", "email", "phone"],
  },
  {
    k: "clinician",
    label: "Clinician",
    desc: "I'm a doctor, surgeon or hospital staffer ordering for clinical use.",
    icon: "Stethoscope",
    image: "consultation",
    eyebrow: "FOR CLINICIANS & HOSPITALS",
    title: "Institutional pricing, on credentials we trust.",
    sub: "Verified IMC or state medical council registration unlocks a 6–10% institutional discount tier and bulk PO ordering.",
    perks: [
      "6–10% institutional discount on most lines",
      "Bulk PO across categories with one statement",
      "Cold-chain validated lanes for biologics",
    ],
    fields: ["full_name", "email", "phone", "council", "council_no", "specialization", "hospital"],
    kyc: { title: "Council registration certificate", sub: "PDF or image · max 5 MB", icon: "BadgeCheck" },
  },
  {
    k: "retailer",
    label: "Retailer",
    desc: "I run a pharmacy or wholesale buying for retail use.",
    icon: "Building",
    image: "warehouseAisle",
    eyebrow: "FOR PHARMACIES & RETAILERS",
    title: "Wholesale margins, with no middlemen.",
    sub: "Upload your CDSCO 20B/21B drug license and GSTIN. We extend Net-30 credit after the qualification window.",
    perks: [
      "Up to 22% margin across categories",
      "Net-30 credit terms (after qualification)",
      "Single PO, one statement, GST-2A reconciled",
    ],
    fields: ["business_name", "owner_name", "email", "phone", "drug_license_no", "gstin", "pan", "address_line", "city", "state", "pin"],
    kyc: { title: "CDSCO 20B/21B drug license", sub: "PDF or image · max 5 MB", icon: "ShieldCheck" },
  },
];

/* ----- helpers ----- */
function getRole(k) { return ROLES.find((r) => r.k === k) || ROLES[0]; }

function pwStrength(pw) {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}
function pwLabel(s) {
  return ["Use 8+ chars, mix case, a digit and a symbol", "Too weak", "Decent — add a symbol", "Strong", "Excellent"][s];
}

/* ----- shared visual side ----- */
function AuthSide({ role, alt }) {
  const r = role || ROLES[0];
  const photo = (IMG[r.image] || IMG.pharmacyCounter)(1400, 1200);
  return (
    <aside className="auth-side">
      <img className="auth-side-photo" src={photo} alt="" />
      <div className="auth-side-overlay" />

      <div className="auth-side-top">
        <Logo dark />
        <a className="auth-side-link" href="/">
          <Icons.ArrowR size={13} stroke={2} style={{ transform: "rotate(180deg)" }} /> Back to home
        </a>
      </div>

      <div className="auth-side-body">
        <div className="auth-side-eyebrow">
          <span className="bar" />
          {r.eyebrow}
        </div>
        <h2 className="auth-side-title">{alt || r.title}</h2>
        <p className="auth-side-sub">{r.sub}</p>

        <div className="auth-perks">
          {r.perks.map((p, i) => (
            <div className="auth-perk" key={i}>
              <div className="auth-perk-ic"><Icons.Check size={14} stroke={2.4} /></div>
              <span>{p}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="auth-side-foot">
        <div className="auth-side-foot-pills">
          <span className="auth-side-foot-pill"><Icons.ShieldCheck size={11} stroke={1.8} /> WHO-GDP</span>
          <span className="auth-side-foot-pill"><Icons.BadgeCheck size={11} stroke={1.8} /> CDSCO 20B/21B</span>
          <span className="auth-side-foot-pill">DPDP Act · 2023</span>
        </div>
      </div>
    </aside>
  );
}

/* ----- LOGIN ----- */
function LoginPage() {
  const [mode, setMode] = aUS("email");
  const [showPw, setShowPw] = aUS(false);
  const [email, setEmail] = aUS("");
  const [phone, setPhone] = aUS("");
  const [pw, setPw] = aUS("");
  const [otpSent, setOtpSent] = aUS(false);
  const [loading, setLoading] = aUS(false);
  const loginMutation = useLogin();

  const submit = async (e) => {
    e.preventDefault();
    if (mode !== "email") {
      // OTP path is Phase 8 — leave the prototype's fake-OTP flow.
      setLoading(true);
      setTimeout(() => {
        toast && toast("Signed in · welcome back");
        setLoading(false);
        setTimeout(() => { window.location.href = "/"; }, 600);
      }, 900);
      return;
    }
    setLoading(true);
    try {
      await loginMutation.mutateAsync({ email, password: pw });
      toast && toast("Signed in · welcome back");
      setTimeout(() => { window.location.href = "/"; }, 400);
    } catch (err) {
      toast && toast(err?.message || "Sign-in failed", "error");
      setLoading(false);
    }
  };
  const sendOtp = () => {
    if (!phone) { toast && toast("Enter your phone"); return; }
    setOtpSent(true);
    toast && toast("OTP sent to " + phone);
  };

  return (
    <div className="auth-shell">
      <section className="auth-form-pane">
        <div className="auth-form-pane-inner">
          <a className="auth-back" href="/"><Icons.ArrowR size={13} stroke={2} style={{ transform: "rotate(180deg)" }} /> Back</a>
          <div>
            <h1 className="auth-h1">Sign in to your account</h1>
            <p className="auth-sub">Welcome back — pick up where you left off. Buyers, clinicians and retailers all sign in here.</p>
          </div>

          <div className="auth-mode-tabs">
            <button type="button" className={"auth-mode-tab" + (mode === "email" ? " is-active" : "")} onClick={() => setMode("email")}>Email</button>
            <button type="button" className={"auth-mode-tab" + (mode === "phone" ? " is-active" : "")} onClick={() => setMode("phone")}>Phone OTP</button>
          </div>

          <form className="auth-form" onSubmit={submit}>
            {mode === "email" && (
              <>
                <div className="field">
                  <label className="field-label">Email</label>
                  <div className="input-affix">
                    <span className="input-affix-ic"><Icons.Mail size={15} stroke={1.7} /></span>
                    <input className="input" type="email" placeholder="you@pharmacy.in" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                </div>
                <div className="field">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <label className="field-label">Password</label>
                    <a href="/pages/auth/forgot-password.html" style={{ fontSize: 12, color: "#0a0a0a", fontWeight: 500 }}>Forgot?</a>
                  </div>
                  <div className="input-affix">
                    <span className="input-affix-ic"><Icons.ShieldCheck size={15} stroke={1.7} /></span>
                    <input className="input" type={showPw ? "text" : "password"} placeholder="••••••••" value={pw} onChange={(e) => setPw(e.target.value)} required />
                    <button type="button" className="input-affix-toggle" onClick={() => setShowPw((v) => !v)} aria-label="Show password">
                      {showPw ? <Icons.X size={14} /> : <Icons.Sparkle size={14} />}
                    </button>
                  </div>
                </div>
                <label className="terms-check">
                  <input type="checkbox" defaultChecked />
                  <span className="filter-check-box"><Icons.Check size={10} stroke={3} /></span>
                  <span>Keep me signed in on this device</span>
                </label>
                <button className="btn btn-primary btn-lg" type="submit" style={{ justifyContent: "center" }} disabled={loading}>
                  {loading ? <><span className="spinner" /> Signing in…</> : <>Sign in <Icons.ArrowR size={14} stroke={2} /></>}
                </button>
              </>
            )}

            {mode === "phone" && !otpSent && (
              <>
                <div className="field">
                  <label className="field-label">Phone</label>
                  <div className="input-affix">
                    <span className="input-affix-ic"><Icons.Phone size={15} stroke={1.7} /></span>
                    <input className="input" type="tel" placeholder="+91 98765 43210" value={phone} onChange={(e) => setPhone(e.target.value)} required />
                  </div>
                  <div className="field-hint">We'll text a 6-digit code · standard SMS rates apply</div>
                </div>
                <button type="button" className="btn btn-primary btn-lg" onClick={sendOtp} style={{ justifyContent: "center" }}>
                  Send OTP <Icons.ArrowR size={14} stroke={2} />
                </button>
              </>
            )}

            {mode === "phone" && otpSent && (
              <>
                <div className="field">
                  <label className="field-label">Enter the 6-digit code we sent to {phone}</label>
                  <OtpInput length={6} />
                  <div className="field-hint" style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                    <span>Code expires in 4:59</span>
                    <button type="button" onClick={() => setOtpSent(false)} style={{ background: "none", border: "none", color: "#0a0a0a", fontWeight: 500, fontSize: 12 }}>Change number</button>
                  </div>
                </div>
                <button type="submit" className="btn btn-primary btn-lg" style={{ justifyContent: "center" }} disabled={loading}>
                  {loading ? <><span className="spinner" /> Verifying…</> : <>Verify & sign in <Icons.Check size={14} stroke={2.4} /></>}
                </button>
              </>
            )}
          </form>

          <div className="auth-divider">OR CONTINUE WITH</div>
          <div className="auth-socials">
            <button className="auth-social-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.44c-.28 1.5-1.12 2.78-2.41 3.62v3.01h3.89c2.28-2.1 3.57-5.2 3.57-8.87z"/><path fill="#34A853" d="M12 24c3.24 0 5.96-1.08 7.94-2.91l-3.89-3.01c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.76-2.1-6.7-4.93H1.27v3.09C3.24 21.3 7.31 24 12 24z"/><path fill="#FBBC04" d="M5.3 14.31c-.23-.7-.36-1.45-.36-2.31s.13-1.61.36-2.31V6.6H1.27C.46 8.21 0 10.05 0 12s.46 3.79 1.27 5.4L5.3 14.3z"/><path fill="#EA4335" d="M12 4.75c1.76 0 3.34.61 4.59 1.79l3.43-3.43C17.95 1.19 15.24 0 12 0 7.31 0 3.24 2.7 1.27 6.6L5.3 9.69c.94-2.83 3.58-4.94 6.7-4.94z"/></svg>
              Google
            </button>
            <button className="auth-social-btn">
              <Icons.BadgeCheck size={16} stroke={1.8} />
              DigiLocker
            </button>
          </div>

          <div style={{ textAlign: "center", fontSize: 13.5, color: "var(--ink-3)" }}>
            New to nethrasap? <a href="/pages/auth/signup.html" style={{ color: "#0a0a0a", fontWeight: 500 }}>Create an account</a>
          </div>
        </div>
      </section>

      <AuthSide role={ROLES[2]} alt="Welcome back. Your supply ledger is right where you left it." />
    </div>
  );
}

/* ----- OTP input ----- */
function OtpInput({ length = 6 }) {
  const refs = aUR([]);
  const [vals, setVals] = aUS(Array(length).fill(""));
  const onInput = (i, v) => {
    const ch = v.slice(-1);
    if (!/\d?/.test(ch)) return;
    setVals((arr) => { const n = [...arr]; n[i] = ch; return n; });
    if (ch && refs.current[i + 1]) refs.current[i + 1].focus();
  };
  const onKey = (i, e) => {
    if (e.key === "Backspace" && !vals[i] && refs.current[i - 1]) refs.current[i - 1].focus();
  };
  return (
    <div className="otp-row">
      {Array.from({ length }).map((_, i) => (
        <input key={i}
               ref={(el) => refs.current[i] = el}
               className="otp-cell mono"
               inputMode="numeric"
               maxLength={1}
               value={vals[i]}
               onChange={(e) => onInput(i, e.target.value)}
               onKeyDown={(e) => onKey(i, e)} />
      ))}
    </div>
  );
}

/* ----- SIGNUP ----- */
function SignupPage() {
  const [step, setStep] = aUS(1);          // 1: role, 2: details, 3: KYC (if needed), 4: success
  const [roleK, setRoleK] = aUS("retailer");
  const [data, setData] = aUS({});
  const [pw, setPw] = aUS("");
  const [showPw, setShowPw] = aUS(false);
  const [agree, setAgree] = aUS(false);
  const [uploaded, setUploaded] = aUS(null);
  const [loading, setLoading] = aUS(false);
  const role = getRole(roleK);
  const totalSteps = role.kyc ? 3 : 2;

  const goNext = () => { setStep((s) => s + 1); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const goBack = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const finish = () => {
    setLoading(true);
    setTimeout(() => { setLoading(false); setStep(totalSteps + 1); }, 1100);
  };

  const update = (key, val) => setData((d) => ({ ...d, [key]: val }));
  const fld = (key, label, type = "text", icon = "User", placeholder = "") => (
    <div className="field">
      <label className="field-label">{label}</label>
      <div className="input-affix">
        <span className="input-affix-ic">{React.createElement(Icons[icon] || Icons.User, { size: 15, stroke: 1.7 })}</span>
        <input className="input" type={type} placeholder={placeholder} value={data[key] || ""} onChange={(e) => update(key, e.target.value)} required />
      </div>
    </div>
  );

  return (
    <div className="auth-shell is-wide">
      <section className="auth-form-pane">
        <div className="auth-form-pane-inner">
          {step > 1 && step <= totalSteps && (
            <button className="auth-back" onClick={goBack} style={{ background: "none", border: "none", padding: 0 }}>
              <Icons.ArrowR size={13} stroke={2} style={{ transform: "rotate(180deg)" }} /> Back
            </button>
          )}
          {step === 1 && (
            <a className="auth-back" href="/"><Icons.ArrowR size={13} stroke={2} style={{ transform: "rotate(180deg)" }} /> Back to home</a>
          )}

          {step <= totalSteps && (
            <div className="signup-stepper">
              {[1, 2, ...(role.kyc ? [3] : [])].map((n, i, arr) => (
                <React.Fragment key={n}>
                  <span className={"signup-step" + (step === n ? " is-active" : step > n ? " is-done" : "")}>
                    <span className="signup-step-num">{step > n ? <Icons.Check size={11} stroke={3} /> : n}</span>
                    {n === 1 ? "Role" : n === 2 ? "Details" : "Verify"}
                  </span>
                  {i < arr.length - 1 && <span className="signup-step-sep" />}
                </React.Fragment>
              ))}
            </div>
          )}

          {step === 1 && (
            <>
              <div>
                <h1 className="auth-h1">Create your account</h1>
                <p className="auth-sub">Pick the role that fits — we'll surface the right pricing tier and the right verification steps for you.</p>
              </div>
              <div className="role-grid">
                {ROLES.map((r) => {
                  const Icon = Icons[r.icon];
                  return (
                    <button key={r.k}
                            className={"role-card" + (roleK === r.k ? " is-selected" : "")}
                            onClick={() => setRoleK(r.k)}>
                      <span className="role-card-ic"><Icon size={22} stroke={1.6} /></span>
                      <span className="role-card-body">
                        <span className="role-card-name">{r.label}</span>
                        <span className="role-card-desc">{r.desc}</span>
                      </span>
                      <span className="role-card-radio" />
                    </button>
                  );
                })}
              </div>
              <button className="btn btn-primary btn-lg" onClick={goNext} style={{ justifyContent: "center" }}>
                Continue as {role.label} <Icons.ArrowR size={14} stroke={2} />
              </button>
              <div style={{ textAlign: "center", fontSize: 13.5, color: "var(--ink-3)" }}>
                Already have an account? <a href="/pages/auth/login.html" style={{ color: "#0a0a0a", fontWeight: 500 }}>Sign in</a>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div>
                <h1 className="auth-h1">Tell us about {roleK === "customer" ? "you" : "your business"}</h1>
                <p className="auth-sub">
                  {roleK === "customer" && "We'll set you up with a customer profile. You can upgrade to clinician or retailer later."}
                  {roleK === "clinician" && "We'll verify your council registration before unlocking institutional pricing."}
                  {roleK === "retailer" && "We'll verify your drug license and GST credentials before unlocking wholesale pricing."}
                </p>
              </div>

              <form className="auth-form" onSubmit={(e) => { e.preventDefault(); goNext(); }}>
                {roleK === "customer" && (
                  <>
                    {fld("full_name", "Full name", "text", "User", "Priya Iyer")}
                    <div className="auth-form-grid">
                      {fld("email", "Email", "email", "Mail", "you@example.in")}
                      {fld("phone", "Phone", "tel", "Phone", "+91 98765 43210")}
                    </div>
                  </>
                )}

                {roleK === "clinician" && (
                  <>
                    {fld("full_name", "Full name", "text", "User", "Dr. Kavita Menon")}
                    <div className="auth-form-grid">
                      {fld("email", "Work email", "email", "Mail", "you@hospital.in")}
                      {fld("phone", "Phone", "tel", "Phone", "+91")}
                    </div>
                    <div className="auth-form-grid">
                      <div className="field">
                        <label className="field-label">Medical council</label>
                        <select className="input" value={data.council || ""} onChange={(e) => update("council", e.target.value)}>
                          <option value="">Select council</option>
                          <option>Medical Council of India (IMC)</option>
                          <option>Karnataka Medical Council</option>
                          <option>Maharashtra Medical Council</option>
                          <option>Tamil Nadu Medical Council</option>
                          <option>Delhi Medical Council</option>
                        </select>
                      </div>
                      {fld("council_no", "Registration number", "text", "Hash", "e.g. KMC-72840")}
                    </div>
                    <div className="auth-form-grid">
                      {fld("specialization", "Specialization", "text", "Stethoscope", "e.g. Endocrinology")}
                      {fld("hospital", "Hospital / Clinic", "text", "Building", "Apollo Hospitals · Bangalore")}
                    </div>
                  </>
                )}

                {roleK === "retailer" && (
                  <>
                    <div className="auth-form-grid">
                      {fld("business_name", "Business name", "text", "Building", "Sunrise Chemists Pvt. Ltd.")}
                      {fld("owner_name", "Signatory / owner name", "text", "User", "Rajesh Mehta")}
                    </div>
                    <div className="auth-form-grid">
                      {fld("email", "Work email", "email", "Mail", "orders@pharmacy.in")}
                      {fld("phone", "Phone", "tel", "Phone", "+91")}
                    </div>
                    <div className="auth-form-grid">
                      {fld("drug_license_no", "Drug license (20B/21B)", "text", "ShieldCheck", "e.g. MH-MUM-2024/2-13841")}
                      {fld("gstin", "GSTIN", "text", "Hash", "27AABCN9012P1Z3")}
                    </div>
                    {fld("pan", "PAN", "text", "BadgeCheck", "AABCN9012P")}
                    {fld("address_line", "Business address", "text", "Pin", "Shop 12, Linking Road, Bandra West")}
                    <div className="auth-form-grid">
                      {fld("city", "City", "text", "Pin", "Mumbai")}
                      {fld("state", "State", "text", "Pin", "Maharashtra")}
                    </div>
                    {fld("pin", "PIN code", "text", "Pin", "400050")}
                  </>
                )}

                <div className="field">
                  <label className="field-label">Choose a password</label>
                  <div className="input-affix">
                    <span className="input-affix-ic"><Icons.ShieldCheck size={15} stroke={1.7} /></span>
                    <input className="input" type={showPw ? "text" : "password"} placeholder="Min 8 characters" value={pw} onChange={(e) => setPw(e.target.value)} required />
                    <button type="button" className="input-affix-toggle" onClick={() => setShowPw((v) => !v)} aria-label="Show password">
                      {showPw ? <Icons.X size={14} /> : <Icons.Sparkle size={14} />}
                    </button>
                  </div>
                  <div className={"pw-meter s" + pwStrength(pw)}>
                    <span /><span /><span /><span />
                  </div>
                  <div className="pw-hint">{pwLabel(pwStrength(pw))}</div>
                </div>

                <label className="terms-check">
                  <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                  <span className="filter-check-box"><Icons.Check size={10} stroke={3} /></span>
                  <span>I agree to nethrasap's <a href="/pages/policy/terms.html">Terms of Service</a> and <a href="/pages/policy/privacy.html">Privacy Policy</a>, including DPDP Act consent for the use of my credentials.</span>
                </label>

                <button type="submit" className="btn btn-primary btn-lg" style={{ justifyContent: "center" }} disabled={!agree || pwStrength(pw) < 2}>
                  {role.kyc ? "Continue to verification" : "Create account"} <Icons.ArrowR size={14} stroke={2} />
                </button>
              </form>
            </>
          )}

          {step === 3 && role.kyc && (
            <>
              <div>
                <h1 className="auth-h1">One last step — verification</h1>
                <p className="auth-sub">Upload your {role.kyc.title.toLowerCase()}. We'll review it within 4 business hours and email you once your account is verified.</p>
              </div>

              <label className={"upload-card" + (uploaded ? " is-done" : "")} onClick={() => setUploaded(uploaded ? null : { name: "drug_license.pdf", size: "2.1 MB" })}>
                <div className="upload-card-left">
                  <div className="upload-card-ic">{React.createElement(Icons[role.kyc.icon] || Icons.Download, { size: 18, stroke: 1.7 })}</div>
                  <div>
                    <div className="upload-card-title">{uploaded ? uploaded.name : role.kyc.title}</div>
                    <div className="upload-card-sub">{uploaded ? "Uploaded · " + uploaded.size : role.kyc.sub}</div>
                  </div>
                </div>
                {uploaded ? <Icons.Check size={18} stroke={2.4} /> : <Icons.Plus size={16} stroke={2} />}
              </label>

              <div className="upload-card" style={{ cursor: "default" }}>
                <div className="upload-card-left">
                  <div className="upload-card-ic"><Icons.Hash size={18} stroke={1.7} /></div>
                  <div>
                    <div className="upload-card-title">PAN card (optional)</div>
                    <div className="upload-card-sub">Speeds up retailer credit approval</div>
                  </div>
                </div>
                <span className="kicker">SKIP</span>
              </div>

              <div className="pdp-trust-row">
                <div className="pdp-trust-item">
                  <Icons.ShieldCheck size={18} stroke={1.6} />
                  <div>
                    <strong>Encrypted upload</strong>
                    <span>TLS 1.3 in transit · AES-256 at rest</span>
                  </div>
                </div>
                <div className="pdp-trust-item">
                  <Icons.Calendar size={18} stroke={1.6} />
                  <div>
                    <strong>4-hour review</strong>
                    <span>You'll get email confirmation</span>
                  </div>
                </div>
              </div>

              <button onClick={finish} className="btn btn-primary btn-lg" style={{ justifyContent: "center" }} disabled={!uploaded || loading}>
                {loading ? <><span className="spinner" /> Submitting…</> : <>Submit for review <Icons.ArrowR size={14} stroke={2} /></>}
              </button>
              <button onClick={finish} className="btn btn-ghost" style={{ justifyContent: "center" }}>I'll do this later</button>
            </>
          )}

          {step > totalSteps && (
            <div className="auth-success">
              <div className="auth-success-ic"><Icons.Check size={32} stroke={2.6} /></div>
              <h1 className="auth-h1">{role.kyc ? "We've got your application" : "Welcome to nethrasap"}</h1>
              <p className="auth-sub">
                {role.kyc
                  ? "We'll review your credentials within 4 business hours. Meanwhile, you can browse the catalog with consumer pricing."
                  : "Your account is live. Same-day dispatch starts at 4 PM IST — let's get you set up."}
              </p>
              <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                <a href="/" className="btn btn-primary">Start browsing <Icons.ArrowR size={14} stroke={2} /></a>
                <a href="/pages/storefront/categories.html" className="btn btn-outline">Explore categories</a>
              </div>
            </div>
          )}
        </div>
      </section>

      <AuthSide role={role} />
    </div>
  );
}

/* ----- FORGOT PASSWORD ----- */
function ForgotPage() {
  const [step, setStep] = aUS(1); // 1: email, 2: otp, 3: new password, 4: done
  const [email, setEmail] = aUS("");
  const [pw, setPw] = aUS("");
  const [showPw, setShowPw] = aUS(false);

  return (
    <div className="auth-shell">
      <section className="auth-form-pane">
        <div className="auth-form-pane-inner">
          <a className="auth-back" href="/pages/auth/login.html"><Icons.ArrowR size={13} stroke={2} style={{ transform: "rotate(180deg)" }} /> Back to sign in</a>

          {step === 1 && (
            <>
              <div>
                <h1 className="auth-h1">Reset your password</h1>
                <p className="auth-sub">Enter your registered email. We'll send a 6-digit code to confirm it's you.</p>
              </div>
              <form className="auth-form" onSubmit={(e) => { e.preventDefault(); setStep(2); }}>
                <div className="field">
                  <label className="field-label">Email</label>
                  <div className="input-affix">
                    <span className="input-affix-ic"><Icons.Mail size={15} stroke={1.7} /></span>
                    <input className="input" type="email" placeholder="you@pharmacy.in" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                </div>
                <button type="submit" className="btn btn-primary btn-lg" style={{ justifyContent: "center" }}>Send code <Icons.ArrowR size={14} stroke={2} /></button>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <div>
                <h1 className="auth-h1">Check your inbox</h1>
                <p className="auth-sub">We sent a 6-digit code to <strong>{email || "your email"}</strong>. Enter it below — the code is valid for 5 minutes.</p>
              </div>
              <form className="auth-form" onSubmit={(e) => { e.preventDefault(); setStep(3); }}>
                <div className="field">
                  <label className="field-label">Verification code</label>
                  <OtpInput length={6} />
                  <div className="field-hint" style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                    <span>Code expires in 4:59</span>
                    <button type="button" onClick={() => setStep(1)} style={{ background: "none", border: "none", color: "#0a0a0a", fontWeight: 500, fontSize: 12 }}>Resend</button>
                  </div>
                </div>
                <button type="submit" className="btn btn-primary btn-lg" style={{ justifyContent: "center" }}>Verify code <Icons.Check size={14} stroke={2.4} /></button>
              </form>
            </>
          )}

          {step === 3 && (
            <>
              <div>
                <h1 className="auth-h1">Set a new password</h1>
                <p className="auth-sub">Choose a password you haven't used before — 8+ characters with a mix of letters, numbers, and a symbol.</p>
              </div>
              <form className="auth-form" onSubmit={(e) => { e.preventDefault(); setStep(4); }}>
                <div className="field">
                  <label className="field-label">New password</label>
                  <div className="input-affix">
                    <span className="input-affix-ic"><Icons.ShieldCheck size={15} stroke={1.7} /></span>
                    <input className="input" type={showPw ? "text" : "password"} placeholder="Min 8 characters" value={pw} onChange={(e) => setPw(e.target.value)} required />
                    <button type="button" className="input-affix-toggle" onClick={() => setShowPw((v) => !v)} aria-label="Show password">
                      {showPw ? <Icons.X size={14} /> : <Icons.Sparkle size={14} />}
                    </button>
                  </div>
                  <div className={"pw-meter s" + pwStrength(pw)}><span /><span /><span /><span /></div>
                  <div className="pw-hint">{pwLabel(pwStrength(pw))}</div>
                </div>
                <div className="field">
                  <label className="field-label">Confirm new password</label>
                  <div className="input-affix">
                    <span className="input-affix-ic"><Icons.Check size={15} stroke={1.7} /></span>
                    <input className="input" type={showPw ? "text" : "password"} placeholder="Type it again" />
                  </div>
                </div>
                <button type="submit" className="btn btn-primary btn-lg" style={{ justifyContent: "center" }} disabled={pwStrength(pw) < 2}>Save new password <Icons.ArrowR size={14} stroke={2} /></button>
              </form>
            </>
          )}

          {step === 4 && (
            <div className="auth-success">
              <div className="auth-success-ic"><Icons.Check size={32} stroke={2.6} /></div>
              <h1 className="auth-h1">Password updated</h1>
              <p className="auth-sub">All set — sign in with your new password to continue.</p>
              <a href="/pages/auth/login.html" className="btn btn-primary" style={{ marginTop: 8 }}>Sign in <Icons.ArrowR size={14} stroke={2} /></a>
            </div>
          )}
        </div>
      </section>

      <AuthSide role={ROLES[0]} alt="A safer ledger, a stronger password." />
    </div>
  );
}

export { LoginPage, SignupPage, ForgotPage };

